import { useMemo } from 'react';
import { SocialPostMedia } from 'fym-shared/src/music24/socialPostFormat';
import { mediaKindOfDraft, PromoteMediaDraft } from '../draftStore';
import { useVideoMeasures } from './useVideoMeasures';

/**
 * The attached media in the shape of the format rule.
 *
 * An image keeps the pixel size that came with it (the kit spec, or the natural size of an
 * uploaded image) and has no duration. A video uses the measures that came with it, and the
 * browser measures (`useVideoMeasures`) for each value that is not known. The browser values are
 * null while the file loads and when it cannot be read.
 */
export function useComposerPostMedia(media: PromoteMediaDraft | undefined): {
  media: SocialPostMedia | null;
  isMeasuring: boolean;
} {
  const kind = media === undefined ? null : mediaKindOfDraft(media);
  const knownWidth = media?.width ?? null;
  const knownHeight = media?.height ?? null;
  const knownDuration = media?.durationSec ?? null;
  const sizeBytes = media?.sizeBytes ?? null;
  const needsMeasures = kind === 'video' && (knownWidth === null || knownHeight === null || knownDuration === null);
  const measured = useVideoMeasures(needsMeasures ? media?.fileUrl : undefined);

  const postMedia = useMemo<SocialPostMedia | null>(() => {
    if (kind === null) {
      return null;
    }
    if (kind === 'image') {
      return { kind: 'image', width: knownWidth, height: knownHeight, durationSeconds: null };
    }
    return {
      kind: 'video',
      width: knownWidth ?? measured.width,
      height: knownHeight ?? measured.height,
      durationSeconds: knownDuration ?? measured.durationSeconds,
      sizeBytes,
    };
  }, [
    kind,
    knownDuration,
    knownHeight,
    knownWidth,
    measured.durationSeconds,
    measured.height,
    measured.width,
    sizeBytes,
  ]);

  return { media: postMedia, isMeasuring: needsMeasures && measured.isLoading };
}
